export type Zone = "CET" | "EET";

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function hm(totalMinutes: number): string {
  const m = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}

const CET_OFFSET_MIN = 0;
const EET_OFFSET_MIN = 60;

function zoneOffset(zone: Zone): number {
  return zone === "CET" ? CET_OFFSET_MIN : EET_OFFSET_MIN;
}

export function qhIntervalLabel(qhIndex: number, zone: Zone): string {
  const startCET = (qhIndex - 1) * 15;
  const endCET = startCET + 15;
  const off = zoneOffset(zone);
  return `${hm(startCET + off)}-${hm(endCET + off)} ${zone}`;
}

export function phIntervalLabel(phIndex: number, zone: Zone): string {
  const startCET = (phIndex - 1) * 60;
  const endCET = startCET + 60;
  const off = zoneOffset(zone);
  return `${hm(startCET + off)}-${hm(endCET + off)} ${zone}`;
}

export function productIntervalLabel(kind: "PH" | "QH", index: number, zone: Zone): string {
  return kind === "PH" ? phIntervalLabel(index, zone) : qhIntervalLabel(index, zone);
}

const DISPLAY_TIME_ZONES: Record<Zone, string> = {
  CET: "Europe/Berlin",
  EET: "Europe/Bucharest",
};

function timeInZone(iso: string, zone: Zone): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "??:??";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: DISPLAY_TIME_ZONES[zone],
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

export function utcIntervalLabel(startIso: string, endIso: string, zone: Zone): string {
  return `${timeInZone(startIso, zone)}-${timeInZone(endIso, zone)} ${zone}`;
}

function tzOffsetMinutes(tz: string, at: Date): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(at);
  const map: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
  const hour = Number(map.hour);
  const asUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    hour === 24 ? 0 : hour,
    Number(map.minute),
    Number(map.second)
  );
  return (asUTC - at.getTime()) / 60000;
}

// Delivery quarters are indexed against CET/CEST (SDAC convention).
// deliveryDate is interpreted as the local CET date; QH-1 = 00:00 on that
// date in Europe/Berlin, shifted +15 min per subsequent index.
export function qhDeliveryUtcStart(deliveryDateISO: string, qhIndex: number): Date {
  const [y, m, d] = deliveryDateISO.split("-").map(Number);
  if (!y || !m || !d) throw new Error(`bad deliveryDate: ${deliveryDateISO}`);
  const probe = new Date(Date.UTC(y, m - 1, d, 0, 0, 0));
  const offset = tzOffsetMinutes("Europe/Berlin", probe);
  const startUtcMs = probe.getTime() - offset * 60000;
  return new Date(startUtcMs + (qhIndex - 1) * 15 * 60000);
}

export type GateStatus = "open" | "closing" | "closed";
const GATE_LEAD_MIN = 60;
const CLOSING_WINDOW_MIN = 60;

export function qhGateStatus(
  deliveryDateISO: string,
  qhIndex: number,
  now: Date
): GateStatus {
  const delivery = qhDeliveryUtcStart(deliveryDateISO, qhIndex);
  const closure = new Date(delivery.getTime() - GATE_LEAD_MIN * 60000);
  const minsToClosure = (closure.getTime() - now.getTime()) / 60000;
  if (minsToClosure <= 0) return "closed";
  if (minsToClosure <= CLOSING_WINDOW_MIN) return "closing";
  return "open";
}

export function qhMinutesToGateClosure(
  deliveryDateISO: string,
  qhIndex: number,
  now: Date
): number {
  const delivery = qhDeliveryUtcStart(deliveryDateISO, qhIndex);
  const closure = new Date(delivery.getTime() - GATE_LEAD_MIN * 60000);
  return (closure.getTime() - now.getTime()) / 60000;
}

// ISO YYYY-MM-DD for "today" in Europe/Bucharest (local delivery date).
export function todayBucharestIso(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Bucharest",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}
